import { useState, useEffect, useCallback } from 'react';
import { History, X } from 'lucide-react';
import { formatDateTime } from '../../utils/auditDiff';
import { ActionBadge, ChangeSummary } from './AuditLog';
import './AuditHistoryModal.css';

function authFetch(url, options = {}) {
  const token = localStorage.getItem('ozel_token');
  return fetch(url, {
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    credentials: 'include',
  });
}

export default function AuditHistoryModal({ entityType, entityId, label, onClose }) {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        entity_type: String(entityType),
        entity_id: String(entityId),
      });
      const res = await authFetch(`/api/audit-logs?${params.toString()}`);
      const data = await res.json();
      setEntries(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('load audit history', err);
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="ahm-overlay" onClick={onClose}>
      <div className="ahm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ahm-head">
          <div>
            <h3><History size={18} /> Geschiedenis</h3>
            <p className="ahm-sub">{entityType}{label ? ` — ${label}` : ''}</p>
          </div>
          <button className="ahm-close" onClick={onClose} aria-label="Sluiten"><X size={18} /></button>
        </div>

        <div className="ahm-body">
          {loading && <div className="ahm-empty">Laden…</div>}
          {!loading && entries.length === 0 && (
            <div className="ahm-empty"><History size={26} /><span>Geen geschiedenis</span></div>
          )}
          {!loading && entries.length > 0 && (
            <ul className="ahm-timeline">
              {entries.map((e) => (
                <li className="ahm-item" key={e.id}>
                  <span className="ahm-dot" />
                  <div className="ahm-item-body">
                    <div className="ahm-item-top">
                      <ActionBadge action={e.action} />
                      <span className="ahm-date">{formatDateTime(e.created_at)}</span>
                    </div>
                    <div className="ahm-account">{e.user_email || '—'}</div>
                    <div className="ahm-change"><ChangeSummary entry={e} /></div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// Drop-in button that other pages can use for per-record history.
export function AuditHistoryButton({ entityType, entityId, label, className = '', iconOnly = false }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        className={`ahm-trigger ${className}`}
        onClick={(e) => { e.stopPropagation?.(); setOpen(true); }}
        title="Geschiedenis / logboek"
      >
        <History size={14} />{!iconOnly && ' Geschiedenis'}
      </button>
      {open && (
        <AuditHistoryModal
          entityType={entityType}
          entityId={entityId}
          label={label}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}
